import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlus, FaEdit, FaTrash, FaTimes, FaToggleOn, FaToggleOff } from "react-icons/fa";
import API from "../api";

export default function CrudPage({ title, endpoint, fields, renderCard }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { mode: 'add'|'edit', data: {} }

  const fetchItems = async () => {
    setLoading(true);
    const { data } = await API.get(`/${endpoint}/all`);
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { fetchItems(); }, []);

  const openAdd = () => {
    const empty = {};
    fields.forEach((f) => { empty[f.key] = f.type === "array" ? [] : ""; });
    setModal({ mode: "add", data: empty });
  };

  const openEdit = (item) => setModal({ mode: "edit", data: { ...item } });

  const handleSave = async () => {
    try {
      if (modal.mode === "add") {
        const { data } = await API.post(`/${endpoint}`, modal.data);
        setItems((prev) => [...prev, data]);
      } else {
        const { data } = await API.put(`/${endpoint}/${modal.data._id}`, modal.data);
        setItems((prev) => prev.map((i) => i._id === data._id ? data : i));
      }
      setModal(null);
    } catch (err) {
      alert(err.response?.data?.message || "Error saving");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await API.delete(`/${endpoint}/${id}`);
    setItems((prev) => prev.filter((i) => i._id !== id));
  };

  const toggleActive = async (item) => {
    const { data } = await API.put(`/${endpoint}/${item._id}`, { ...item, active: !item.active });
    setItems((prev) => prev.map((i) => i._id === data._id ? data : i));
  };

  const updateField = (key, value) => setModal((prev) => ({ ...prev, data: { ...prev.data, [key]: value } }));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white">{title}</h1>
          <p className="text-gray-400 text-sm">{items.length} items</p>
        </div>
        <motion.button onClick={openAdd} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
          className="btn-primary px-4 py-2.5 rounded-xl text-white text-sm font-semibold flex items-center gap-2">
          <FaPlus className="text-xs" /> <span>Add New</span>
        </motion.button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.length === 0 && (
            <div className="col-span-3 text-center text-gray-500 py-12">No items yet. Click "Add New" to get started.</div>
          )}
          {items.map((item) => (
            <motion.div key={item._id} layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className={`glass rounded-2xl p-5 border border-white/10 relative ${!item.active ? "opacity-50" : ""}`}>
              {renderCard(item)}
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/10">
                <button onClick={() => toggleActive(item)} className="text-lg" title={item.active ? "Deactivate" : "Activate"}>
                  {item.active ? <FaToggleOn className="text-green-400" /> : <FaToggleOff className="text-gray-500" />}
                </button>
                <button onClick={() => openEdit(item)}
                  className="flex-1 py-1.5 glass rounded-lg text-blue-400 hover:text-blue-300 text-xs font-semibold flex items-center justify-center gap-1">
                  <FaEdit /> Edit
                </button>
                <button onClick={() => handleDelete(item._id)}
                  className="flex-1 py-1.5 glass rounded-lg text-red-400 hover:text-red-300 text-xs font-semibold flex items-center justify-center gap-1">
                  <FaTrash /> Delete
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {modal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setModal(null)}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto glass rounded-2xl p-6 border border-white/10"
              style={{ background: "rgba(10,15,30,0.97)" }}>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-white font-bold text-lg">{modal.mode === "add" ? "Add" : "Edit"} {title.slice(0, -1)}</h2>
                <button onClick={() => setModal(null)} className="text-gray-400 hover:text-white"><FaTimes /></button>
              </div>

              <div className="space-y-4">
                {fields.map((field) => (
                  <div key={field.key}>
                    <label className="text-gray-400 text-xs mb-1.5 block">{field.label}</label>
                    {field.type === "textarea" ? (
                      <textarea rows={3} value={modal.data[field.key] || ""}
                        onChange={(e) => updateField(field.key, e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm resize-none" />
                    ) : field.type === "array" ? (
                      <div>
                        <textarea rows={3} value={(modal.data[field.key] || []).join("\n")}
                          onChange={(e) => updateField(field.key, e.target.value.split("\n").filter(Boolean))}
                          placeholder="One item per line"
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm resize-none" />
                        <p className="text-gray-600 text-xs mt-1">One item per line</p>
                      </div>
                    ) : field.type === "color" ? (
                      <div className="flex gap-3 items-center">
                        <input type="color" value={modal.data[field.key] || "#0ea5e9"}
                          onChange={(e) => updateField(field.key, e.target.value)}
                          className="w-10 h-10 rounded-lg border-0 cursor-pointer bg-transparent" />
                        <input type="text" value={modal.data[field.key] || ""}
                          onChange={(e) => updateField(field.key, e.target.value)}
                          className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 text-sm" />
                      </div>
                    ) : field.type === "number" ? (
                      <input type="number" value={modal.data[field.key] || ""}
                        onChange={(e) => updateField(field.key, Number(e.target.value))}
                        min={field.min} max={field.max}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 text-sm" />
                    ) : (
                      <input type="text" value={modal.data[field.key] || ""}
                        onChange={(e) => updateField(field.key, e.target.value)}
                        placeholder={field.placeholder || ""}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm" />
                    )}
                  </div>
                ))}
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setModal(null)}
                  className="flex-1 py-2.5 glass rounded-xl text-gray-400 hover:text-white text-sm font-semibold">
                  Cancel
                </button>
                <motion.button onClick={handleSave} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  className="flex-1 btn-primary py-2.5 rounded-xl text-white text-sm font-semibold">
                  <span>Save</span>
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
